import React, { useState, useEffect } from 'react';
import { SpinMachine, GetConfig, StopReel } from './../../../utilities/BL/Rollers';
import { ConnectWebSocketForOverlay, DisconnectWebSocket, SetSocketMessageHandler, ClearSocketMessageHandler } from './../../../utilities/BL/Zones';

export const RollersFan = ({ screenId: propScreenId }) => {
    const queryParams = new URLSearchParams(window.location.search);
    const screenId = parseInt(queryParams.get('screenId') || propScreenId || 1, 10);

    const [gameState, setGameState] = useState('idle'); // 'idle', 'spinning'
    const [stoppingReelIndex, setStoppingReelIndex] = useState(0); // 0 a rollersCount - 1
    const [isLocked, setIsLocked] = useState(false);
    const [rollersCount, setRollersCount] = useState(3);
    const [celebrationTimeMs, setCelebrationTimeMs] = useState(6000);
    const [toast, setToast] = useState('');
    const [theme, setTheme] = useState(null);

    const getMediaUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http://') || path.startsWith('https://')) return path;
        const apiBase = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        return `${apiBase}${path}`;
    };

    // Leads Form
    const [requireLeads, setRequireLeads] = useState(false); 
    const [hasSubmittedLeads, setHasSubmittedLeads] = useState(false);
    const [playerData, setPlayerData] = useState({ name: '', contact: '' });

    useEffect(() => {
        loadScreenTheme();

        const connectWS = async () => {
            const overlayType = `rollers:fan:${screenId}`;
            await ConnectWebSocketForOverlay(
                overlayType,
                () => {},
                (toastMsg) => console.log(`[RollersFan WS] ${toastMsg}`)
            );
            SetSocketMessageHandler(handleIncomingMessage);
        };
        connectWS();

        return () => {
            ClearSocketMessageHandler();
            DisconnectWebSocket();
        };
    }, [screenId]);

    const handleIncomingMessage = (message) => {
        if (!message || !message.type) return;

        const type = message.type.toLowerCase();

        // 1. Recargar configuración y tema del aficionado en vivo al proyectar o iniciar giros
        if (type === 'rollers.prepare' || type === 'rollers.cover' || type === 'rollers.spin') {
            loadScreenTheme();
        }

        // 2. Restablecer botones si es un comando de reinicio/preparación
        if (type === 'rollers.prepare') {
            setGameState('idle');
            setStoppingReelIndex(0);
            setIsLocked(false);
        }
    };

    const loadScreenTheme = async () => {
        const configRes = await GetConfig(screenId);
        if (configRes.success && configRes.data) {
            setRollersCount(configRes.data.rollersCount || 3);
            setCelebrationTimeMs(configRes.data.celebrationTimeMs || 6000);
            if (configRes.data.themeId) {
                fetchTheme(configRes.data.themeId);
            }
        }
    };

    const fetchTheme = async (themeId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}Rollers/GetTheme/${themeId}`);
            const data = await response.json();
            if (data.success) {
                setTheme(data.data);
            }
        } catch (err) {
            console.error("Error al obtener tema del aficionado:", err);
        }
    };

    const showToast = (msg) => {
        setToast(msg);
        setTimeout(() => setToast(''), 3000);
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        if (!playerData.name.trim()) {
            showToast('Por favor, ingresa tu nombre');
            return;
        }
        setHasSubmittedLeads(true);
        showToast(`¡Listo ${playerData.name}! Presiona el botón.`);
    };

    const handleSpinClick = async () => {
        if (isLocked) return;

        if (gameState === 'idle') {
            setIsLocked(true);
            showToast('¡Suerte!');

            try {
                const requestBody = {
                    targetScreenId: parseInt(screenId, 10) || 0,
                    playerName: playerData.name || "Aficionado Anónimo",
                    playerContact: playerData.contact || "" 
                };

                const result = await SpinMachine(requestBody);

                if (result.success) {
                    setGameState('spinning');
                    setStoppingReelIndex(0);
                    setTimeout(() => setIsLocked(false), 1200); // 1.2s lock to avoid spam
                } else {
                    showToast(`❌ Error: ${result.message || 'Fallo al tirar'}`);
                    setIsLocked(false);
                }

            } catch (error) {
                console.error("Error al tirar:", error);
                showToast('❌ Error de conexión');
                setIsLocked(false);
            }
        } else if (gameState === 'spinning') {
            setIsLocked(true);
            showToast(`Deteniendo Rodillo ${stoppingReelIndex + 1}...`);

            try {
                const result = await StopReel(screenId, stoppingReelIndex);
                if (result.success) {
                    const nextIndex = stoppingReelIndex + 1;
                    setStoppingReelIndex(nextIndex);

                    if (nextIndex >= rollersCount) {
                        // Todos los rodillos se detuvieron. Esperar el tiempo de celebración antes de reiniciar.
                        setTimeout(() => {
                            setGameState('idle');
                            setStoppingReelIndex(0);
                            setIsLocked(false);
                            if (requireLeads) {
                                setPlayerData({ name: '', contact: '' });
                                setHasSubmittedLeads(false);
                            } else {
                                showToast('¡Giro completado!');
                            }
                        }, celebrationTimeMs);
                    } else {
                        // Esperar 1.2s antes de permitir parar el siguiente
                        setTimeout(() => setIsLocked(false), 1200);
                    }
                } else {
                    showToast('❌ Error al parar rodillo');
                    setIsLocked(false);
                }
            } catch (error) {
                console.error("Error al detener rodillo:", error);
                showToast('❌ Error de conexión');
                setIsLocked(false);
            }
        }
    };

    // Renderizado del Fondo del Tema Móvil
    const renderFanBackground = () => {
        if (!theme) return <div className="SelfieFan__Screen__QR__Background" style={{ backgroundColor: '#111' }} />;

        if (theme.fanBackgroundType === 'video' && theme.fanBackgroundUrl) {
            return (
                <div className="SelfieFan__Screen__QR__Background">
                    <video 
                        src={getMediaUrl(theme.fanBackgroundUrl)} 
                        className="SelfieFan__Screen__QR__Background__Video" 
                        autoPlay loop muted playsInline 
                    />
                </div>
            );
        } else if (theme.fanBackgroundType === 'image' && theme.fanBackgroundUrl) {
            return (
                <div className="SelfieFan__Screen__QR__Background">
                    <img 
                        src={getMediaUrl(theme.fanBackgroundUrl)} 
                        className="SelfieFan__Screen__QR__Background__Video" 
                        alt="Background" 
                    />
                </div>
            );
        } else {
            return (
                <div 
                    className="SelfieFan__Screen__QR__Background" 
                    style={{ backgroundColor: theme.fanBackgroundColor || '#1a1a1a' }} 
                />
            );
        }
    };

    // VISTA 1: FORMULARIO DE LEADS
    if (requireLeads && !hasSubmittedLeads) {
        return (
            <div className="slot-fan-container slot-fan-leads">
                {renderFanBackground()}
                <div className="casino-card text-center position-relative z-10">
                    <i className="bi bi-person-vcard text-warning display-1 mb-3"></i>
                    <h2 className="text-white fw-bold text-uppercase mb-2">¡Participa y Gana!</h2>
                    <p className="text-white-50 mb-4">Regístrate rápido para probar tu suerte en la máquina.</p>
                    
                    <form onSubmit={handleFormSubmit}>
                        <input 
                            type="text" 
                            className="form-control form-control-lg mb-3 bg-dark text-white border-warning" 
                            placeholder="Tu Nombre" 
                            value={playerData.name}
                            onChange={(e) => setPlayerData({...playerData, name: e.target.value})}
                            required
                        />
                        <input 
                            type="email" 
                            className="form-control form-control-lg mb-4 bg-dark text-white border-warning" 
                            placeholder="Tu Correo (Opcional)" 
                            value={playerData.contact}
                            onChange={(e) => setPlayerData({...playerData, contact: e.target.value})}
                        />
                        <button type="submit" className="btn btn-warning btn-lg w-100 fw-bold fs-4 text-dark text-uppercase">
                            Jugar Ahora <i className="bi bi-play-circle-fill"></i>
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    // VISTA 2: BOTÓN DE JUEGO (MÓVIL)
    return (
        <div className="slot-fan-container d-flex flex-column justify-content-between align-items-center py-4 px-3 vh-100 text-white position-relative">
            {renderFanBackground()}

            {/* Header */}
            <header className="w-100 text-center position-relative z-10 mb-2">
                <img src="/img/page/adsl-five.png" alt="Logo Adsl Five" className="slot-fan-logo mx-auto mb-2" style={{ maxHeight: '50px' }} />
                <p className="small text-white-50">Jugando en la Pantalla #{screenId}</p>
                <h3 className="text-xl font-bold uppercase mt-4 text-warning">
                    {playerData.name ? `¡Vamos ${playerData.name}!` : 'PROBAR SUERTE'}
                </h3>
            </header>

            {/* Botón de Tirar (Spin Button) */}
            <div className="d-flex flex-column align-items-center justify-content-center flex-grow-1 position-relative z-10 w-100">
                <button
                    onClick={handleSpinClick}
                    disabled={isLocked}
                    className={`rollers-fan-spin-btn ${isLocked ? 'spinning' : 'ready'}`}
                >
                    <div className="inner-circle d-flex flex-column align-items-center justify-content-center">
                        <i className={`bi ${gameState === 'spinning' ? 'bi-hand-index' : 'bi-hand-index-thumb-fill'} text-4xl mb-1`}></i>
                        <span className="font-extrabold tracking-widest text-center text-sm" style={{ whiteSpace: 'pre-line' }}>
                            {isLocked ? 'ESPERA...' : (gameState === 'spinning' ? `PARAR\nRODILLO ${stoppingReelIndex + 1}` : 'GIRAR')}
                        </span>
                    </div>
                </button>

                <p className="mt-4 text-center text-sm font-semibold opacity-75">
                    {gameState === 'spinning' ? '¡Presiona el botón para detener el siguiente rodillo!' : 'Presiona el botón para iniciar el juego'}
                </p>
            </div>

            {/* Toast Container */}
            {toast && (
                <div className="position-absolute bottom-0 mb-5 bg-black border border-warning text-warning font-weight-bold px-4 py-2 rounded-pill text-center z-50">
                    {toast}
                </div>
            )}
        </div>
    );
};
